/// <amd-module name="Build/Designer/Services/PipelineEditorActionsViewService" />
import { Build, BuildDefinition, BuildDefinitionVariable, DefinitionQueueStatus, YamlProcess } from "Build/Client/Contracts/Build";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { BuildOptionGUIDs } from "Build/Common/Library/Constants";
import { ILanguageEditorService } from "Build/Common/Library/Contracts";
import { BuildMessageInfo, SaveResult } from "Build/Common/Library/Designer.types";
import { IInstallAppService } from "Build/Common/Library/InstallAppService";
import { PipelineSettings, PipelineSettingsPanel } from "Build/Common/Library/Legacy/Components/PipelineSettingsPanel";
import { IPipelineResourcesService } from "Build/Common/Library/PipelineResourcesService";
import { IPipelineService } from "Build/Common/Library/PipelineService";
import * as LibraryResources from "Build/Common/Library/Resources";
import { RepositoryDescriptor } from "Build/Common/Library/Types";
import { EditorConstants } from "Build/Designer/Constants";
import { PipelineVariablesPanel } from "Build/Designer/PipelineVariablesPanel";
import * as Resources from "Build/Designer/Resources";
import { SavePanel } from "Build/Designer/SavePanel";
import { CommitDescriptor, IYamlFileService } from "Build/Designer/YamlFileService";
import { QueuePanel } from "Build/Queue-Panel/QueuePanel";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { RepositoryResourceParameters, Run, RunPipelineParameters, RunResourcesParameters } from "Pipelines/Client/Contracts/Pipelines";
import { IPipelinesRestClient } from "Pipelines/Client/RestClient/Pipelines";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Uri } from "VSS/Platform/Util/Url";
import { Dialog } from "VSSUI/Dialog";
import { Observer } from "VSSUI/Observer";
import * as React from "react";
import { ValidationErrors } from "../Components/ValidationErrors";
import { getFileName } from "../Util/File";
import { IPipelineEditorViewService, getPipelineEditorViewService } from "./PipelineEditorViewService";
import { ContentSize } from "VSSUI/Callout";
import { TitleSize } from "VSSUI/Header";
import { CustomPanel, PanelHeader, PanelContent, PanelFooter } from "VSSUI/Panel";
import { Status, StatusSize, Statuses } from "VSSUI/Status";

export interface IPipelineEditorActionsViewService extends IVssService {
    authorizeAndRun(): void;
    canSwitchToAppConnection(): IReadonlyObservableValue<boolean>;
    canInstallApp(): IReadonlyObservableValue<boolean>;
    canValidate(): boolean;
    checkForAppConnectionUpgrade(): void;
    dismissAppConnectionRecommendations(): void;
    dismissBuildMessage(): void;
    downloadYaml(): void;
    getBuildMessage(): BuildMessageInfo | undefined;
    getIsRunning(): IReadonlyObservableValue<boolean>;
    getIsSaving(): IReadonlyObservableValue<boolean>;
    installApp(): void;
    runBuildWithParameters(): void;
    showQueuePanel(): void;
    showSavePanel(): void;
    showValidateAndSavePanel(): void;
    showSettingsPanel(): void;
    showVariablesPanel(): void;
    validateYaml(): Promise<any | undefined>;
    validateAndUpdateSchema(showDialog: boolean): Promise<object | undefined>;
    setEditor(editor: monaco.editor.IStandaloneCodeEditor | undefined): void;
    switchToAppConnection(): void;
    openTemplate(templatePath: string): void;
    checkTemplate(template: string): boolean | undefined;
}

interface ServiceConnectionRecommendation {
    commonConnectionId: string;
    userConnectionId: string;
    appInstalled: boolean;
    appInstalledForRepository: boolean;
    installAppUrlRoot: string;
    userCanInstall: boolean;
    repositoryMappedToAnotherCollection: boolean;
}

export class TemplateDetails {
    name: string;
    path: string;
    endpoint: string;
    repo: string;
    ref: string;
    type: string;
    exists: boolean;
}

class PipelineEditorActionsViewService extends VssService implements IPipelineEditorActionsViewService {
    private buildMessageInfo?: BuildMessageInfo;
    private isBuildRunning = new ObservableValue(false);
    private editor?: monaco.editor.IStandaloneCodeEditor;
    private panelErrorMessage = new ObservableValue("");
    private connectionRecommendation: ServiceConnectionRecommendation | undefined;
    private isYamlDirty = new ObservableValue(false);
    private isSaving = new ObservableValue(false);
    private layoutManager: IVssLayoutManager;
    private pipelineService: IPipelineService;
    private recommendSwitchToAppConnection = new ObservableValue(false);
    private recommendInstallApp = new ObservableValue(false);
    private viewService: IPipelineEditorViewService;
    private templates: { [templatePath: string]: TemplateDetails } = {};
    private validateAndUpdateExternalSources: boolean;
    private isValidateAndSavePipelineByDefaultEnabled: boolean;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.pipelineService = this.pageContext.getService<IPipelineService>("IPipelineService");
        this.viewService = getPipelineEditorViewService(pageContext);
        this.validateAndUpdateExternalSources = isFeatureFlagEnabled(this.pageContext, "Build2.ValidateAndUpdateExternalSources", false);
        this.isValidateAndSavePipelineByDefaultEnabled = isFeatureFlagEnabled(this.pageContext, "Build2.ValidateAndSavePipelineByDefault", false);
    }

    public async authorizeAndRun() {
        if (this.isYamlDirty.value) {
            (await this.isValidateAndSavePipelineByDefaultEnabled) ? this.showValidateAndSavePanel() : this.showSavePanel();
        }

        const pipelineResourcesService = this.pageContext.getService<IPipelineResourcesService>("IPipelineResourcesService");
        await pipelineResourcesService.autoAuthorizeResources(this.viewService.getDefinition().id, this.viewService.getBranch()).then(
            () => {},
            (error: Error) => {
                console.warn("YAML editor: Error while auto-authorizing resources: " + error && error.message);
            }
        );

        this.isBuildRunning.value = true;
        this.viewService.clearEditorError();

        this.buildMessageInfo = {
            id: -1,
            number: "",
            isQueuing: true
        };

        this.editor?.layout();

        this.pipelineService.runPipeline(this.viewService.getDefinition(), this.viewService.getBranch()).then(
            build => {
                if (build) {
                    this.buildMessageInfo = {
                        id: build.id,
                        number: build.buildNumber
                    };
                } else {
                    this.viewService.handleRunBuildError();
                }
                this.isBuildRunning.value = false;
            },
            (error: Error) => {
                this.isBuildRunning.value = false;
                this.viewService.handleRunBuildError(error);
            }
        );
    }

    public canSwitchToAppConnection() {
        return this.recommendSwitchToAppConnection;
    }

    public canInstallApp() {
        return this.recommendInstallApp;
    }

    public canValidate() {
        return this.validateAndUpdateExternalSources || equals(this.viewService.getDefinition().repository.type, RepositoryTypes.TfsGit, true);
    }

    public async checkForAppConnectionUpgrade() {
        const connection = this.viewService.getConnection();
        if (
            isFeatureFlagEnabled(this.pageContext, "Build2.SwitchToAppConnection", false) &&
            !this.viewService.shouldShowLandingPage() &&
            !connection.isAppConnection &&
            connection.appName
        ) {
            try {
                this.connectionRecommendation = await this.getRecommendedServiceConnection();
                if (this.connectionRecommendation.commonConnectionId) {
                    // there is an app connection available
                    if (connection.autoUpgradeToAppConnection) {
                        // automatically switch to start using it, the user has likely gone through 'installApp'
                        this.switchToAppConnection();
                    } else {
                        // there is an existing app connection, so offer to change to that connection
                        this.recommendSwitchToAppConnection.value = true;
                    }
                } else if (!this.connectionRecommendation.appInstalledForRepository && this.connectionRecommendation.userCanInstall) {
                    // the App is not installed, but the user is able to install it, so offer to install it
                    this.recommendInstallApp.value = true;
                }
            } catch (error) {
                const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService.publishEvent("Build", "Editor.CheckForAppConnectionUpgradeError", {
                    message: error && error.message,
                    error
                });
            }
        }
    }

    public dismissAppConnectionRecommendations() {
        if (this.recommendSwitchToAppConnection.value) {
            this.recommendSwitchToAppConnection.value = false;
        }
        if (this.recommendInstallApp.value) {
            this.recommendInstallApp.value = false;
        }

        this.editor?.layout();
    }

    public async installApp() {
        const definition = this.viewService.getDefinition();
        const connection = this.viewService.getConnection();
        const installAppService = this.pageContext.getService<IInstallAppService>("IInstallAppService");
        installAppService.installAppForEdit(definition.id, connection.nonce, this.connectionRecommendation!.installAppUrlRoot, definition.repository);
    }

    public async switchToAppConnection() {
        const connectionId = this.connectionRecommendation!.commonConnectionId;
        const definition = { ...this.viewService.getDefinition() };
        definition.repository.properties = definition.repository.properties || [];
        definition.repository.properties["connectedServiceId"] = connectionId;

        this.dismissAppConnectionRecommendations();
        this.isSaving.value = true;

        try {
            const savedDefinition = await this.pipelineService.savePipeline(definition);

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            await contributionService.getDataAsync<boolean>(
                "ms.vss-build-web.pipeline-edit-continuation-data-provider",
                {
                    complete: true
                },
                true
            );

            this.isSaving.value = false;
            this.viewService.updateSavedDefinition(savedDefinition);
            this.dismissAppConnectionRecommendations();

            const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent("Build", "Editor.AppConnectionUpgradeFromOAuth", {
                GitHubApp: "AddAppConnection",
                GitHubAppRepo: savedDefinition.repository.id,
                action: "ChangeConnectionToGitHubApp"
            });

            // show a toast on complete if this was a user-driven action
            const connection = this.viewService.getConnection();
            if (!connection.autoUpgradeToAppConnection) {
                this.showToast(LibraryResources.ConnectionChanged);
            }
        } catch (e) {
            this.showToast((e && e.message) || LibraryResources.SavePipelineErrorMessage);
        }
    }

    public dismissBuildMessage() {
        this.buildMessageInfo = undefined;
    }

    public getBuildMessage() {
        return this.buildMessageInfo;
    }

    public getIsRunning() {
        return this.isBuildRunning;
    }

    public getIsSaving() {
        return this.isSaving;
    }

    public runBuildWithParameters() {
        const definition = this.viewService.getDefinition();
        if (!definition) {
            return;
        }

        this.isBuildRunning.value = true;
        this.viewService.clearEditorError();
        this.buildMessageInfo = undefined;

        this.layoutManager.renderCallout(onDismiss => (
            <CommandComponent
                commandType="ciQueueBuildComponent"
                definition={definition}
                repository={this.viewService.getRepository()}
                processType={definition.process.type}
                pageContext={this.pageContext}
                project={definition.project.id}
                source="yamlEditor"
                onSuccess={(build: Build) => {
                    if (build) {
                        this.buildMessageInfo = {
                            id: build.id,
                            number: build.buildNumber
                        };

                        this.editor?.layout();
                    } else {
                        this.viewService.handleRunBuildError();
                    }
                }}
                onClosed={() => {
                    this.isBuildRunning.value = false;
                    onDismiss();
                }}
                definitionVariables={definition.variables}
                queue={definition.queue}
                defaultSourceBranch={this.viewService.getBranch()}
                onDismiss={onDismiss}
            />
        ));
    }

    public showQueuePanel() {
        const definition = this.viewService.getDefinition();
        this.layoutManager.renderCallout(onDismiss => (
            <QueuePanel
                showPanel={true}
                pageContext={this.pageContext}
                projectId={definition.project.id}
                definitionId={definition.id}
                buildRepository={this.viewService.getRepository()}
                sourceBranch={this.viewService.getBranch()}
                runtimeVariables={getOverridableVariables(definition.variables)}
                onDismiss={onDismiss}
            />
        ));
    }

    public showSavePanel() {
        this.isSaving.value = false;
        const isNewFile = this.viewService.getIsNewFile();
        const shortFileName = getFileName(this.viewService.getYamlPath());
        const messageText = isNewFile ? Resources.SavePanelCommitMessageForAdd : Resources.SavePanelCommitMessageForEdit;
        const commitMessage = shortFileName ? format(messageText, shortFileName) : "";
        const canCreatePr =
            !equals(this.viewService.getRepository().type, RepositoryTypes.Bitbucket, true) ||
            isFeatureFlagEnabled(this.pageContext, "Build2.BitbucketAzurePipelinesOAuthClient", false);

        this.layoutManager.renderCallout(onDismiss => (
            <Observer isSaving={this.isSaving} errorMessage={this.panelErrorMessage}>
                {({ errorMessage, isSaving }: { errorMessage: string; isSaving: boolean }) => (
                    <SavePanel
                        errorMessage={errorMessage}
                        fileName={this.viewService.getYamlPath()}
                        commitMessage={commitMessage}
                        defaultBranch={this.viewService.getBranch()}
                        isOpen={true}
                        isSaving={isSaving}
                        saveAndRun={false}
                        canCreatePr={canCreatePr}
                        onSave={descriptor => this.saveYaml(descriptor, onDismiss)}
                        onDismiss={() => {
                            if (isSaving) {
                                return;
                            }

                            this.panelErrorMessage.value = "";
                            onDismiss();
                        }}
                        showCancelButton={true}
                    />
                )}
            </Observer>
        ));
    }

    public showValidateAndSavePanel() {
        this.isSaving.value = false;
        const isNewFile = this.viewService.getIsNewFile();
        const shortFileName = getFileName(this.viewService.getYamlPath());
        const messageText = isNewFile ? Resources.SavePanelCommitMessageForAdd : Resources.SavePanelCommitMessageForEdit;
        const commitMessage = shortFileName ? format(messageText, shortFileName) : "";
        const canCreatePr =
            !equals(this.viewService.getRepository().type, RepositoryTypes.Bitbucket, true) ||
            isFeatureFlagEnabled(this.pageContext, "Build2.BitbucketAzurePipelinesOAuthClient", false);

        this.layoutManager.renderCallout(onDismiss => (
            <Observer isSaving={this.isSaving} errorMessage={this.panelErrorMessage}>
                {({ errorMessage, isSaving }: { errorMessage: string; isSaving: boolean }) => (
                    <SavePanel
                        isValidateAndSave={true}
                        onValidate={() => this.validateYaml()}
                        errorMessage={errorMessage}
                        fileName={this.viewService.getYamlPath()}
                        commitMessage={commitMessage}
                        defaultBranch={this.viewService.getBranch()}
                        isOpen={true}
                        isSaving={isSaving}
                        saveAndRun={false}
                        canCreatePr={canCreatePr}
                        onSave={descriptor => this.saveYaml(descriptor, onDismiss)}
                        onDismiss={() => {
                            if (isSaving) {
                                return;
                            }

                            this.panelErrorMessage.value = "";
                            onDismiss();
                        }}
                        showCancelButton={true}
                    />
                )}
            </Observer>
        ));
    }

    public showSettingsPanel() {
        const definition = this.viewService.getDefinition();
        this.layoutManager.renderCallout(onDismiss => (
            <Observer isSaving={this.isSaving} errorMessage={this.panelErrorMessage}>
                {({ errorMessage, isSaving }: { errorMessage: string; isSaving: boolean }) => (
                    <PipelineSettingsPanel
                        key={definition.revision}
                        branch={this.viewService.getBranch()}
                        definition={definition}
                        errorMessage={errorMessage}
                        isOpen={true}
                        isSaving={isSaving}
                        isYamlDirty={this.isYamlDirty.value}
                        onDismiss={() => {
                            if (isSaving) {
                                return;
                            }

                            this.panelErrorMessage.value = "";
                            onDismiss();
                        }}
                        onError={message => (this.panelErrorMessage.value = message)}
                        onSave={changes => this.onSavePipelineSettings(changes, onDismiss)}
                    />
                )}
            </Observer>
        ));
    }

    public async downloadYaml() {
        const definition = this.viewService.getDefinition();
        const exportedFileName = definition.name.concat(".yml");
        try {
            const result = await this.getYaml();
            this.downloadFile(exportedFileName, result.finalYaml);
        } catch (ex) {
            this.showDialog(ex.message);
            this.downloadFile(exportedFileName, ex.message);
        }
    }

    public async validateYaml(): Promise<any | undefined> {
        if (!this.canValidate()) {
            return;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.yaml-schema-data-provider";
        const yaml = this.viewService.getYamlContent().value;
        const definition = this.viewService.getDefinition();
        const parameters = {
            projectId: definition.project.id,
            pipelineId: definition.id,
            branch: this.viewService.getBranch(),
            yaml: yaml,
            yamlFileName: this.viewService.getTemplatePath()
        };

        return await contributionService.getDataAsync<any>(dataProviderId, parameters, true);
    }

    public async validateAndUpdateSchema(showValidationResult: boolean): Promise<any | undefined> {
        const result = await this.validateYaml();

        if (result.schema) {
            // Update the schema if there are no parsing errors or if showValidationResult is false which indicates initial load.
            // If there are errors, the schema will be generic without template data, so updating the schema may result in a loss of intellisense
            if (!result.errors || !showValidationResult) {
                const contributionService: IVssContributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                contributionService
                    .getService<ILanguageEditorService>("ms.vss-build-web.language-editor-service")
                    .then((service: ILanguageEditorService) => {
                        service?.useSchema(result.schema);
                    });
            }
        }

        // If no errors, update the template list.  Otherwise keep the previous list since it may have more data
        if (!result.errors && result.templates) {
            this.templates = result.templates as { [path: string]: TemplateDetails };
        }

        if (showValidationResult) {
            if (result.errors) {
                const errorList = result.errors.split("\n");
                this.isValidateAndSavePipelineByDefaultEnabled ? this.showValidationPanel(errorList) : this.showDialog(result.errors);
            } else {
                this.isValidateAndSavePipelineByDefaultEnabled ? this.showValidationPanel([]) : this.showDialog(Resources.OK);
            }
            
        }
        return result.templates;
    }

    public showYamlDialog() {}

    public showVariablesPanel() {
        const definition = this.viewService.getDefinition();
        this.layoutManager.renderCallout(onDismiss => (
            <Observer isSaving={this.isSaving} errorMessage={this.panelErrorMessage}>
                {({ errorMessage, isSaving }: { errorMessage: string; isSaving: boolean }) => (
                    <PipelineVariablesPanel
                        errorMessage={errorMessage}
                        isSaving={isSaving}
                        onDismiss={() => {
                            if (isSaving) {
                                return;
                            }

                            this.panelErrorMessage.value = "";
                            onDismiss();
                        }}
                        onSave={variables => this.onSavePipelineVariables(variables, onDismiss)}
                        variables={definition.variables}
                    />
                )}
            </Observer>
        ));
    }

    public setEditor(editor: monaco.editor.IStandaloneCodeEditor | undefined) {
        if (this.editor && !editor) {
            this.viewService.setYamlContent(this.editor.getValue());
        }

        this.editor = editor;
    }

    public openTemplate = (templatePath: string) => {
        const relativeUrl = this.viewService.getDefinitionYamlEditorUrl();
        const uri = new Uri(relativeUrl);

        let template = this.templates[templatePath];

        if (!template) {
            // If we don't know whether the template exists or not, we should not reach this point.
            // The codelens will say "Validate", and that call will provide the template info.
            return;
        }

        // strip off the resource information from the template path (ie: template.yml@repo)
        uri.addQueryParam("template", template.path);

        if (template.endpoint) {
            uri.addQueryParam("endpoint", template.endpoint);
        } else if (
            this.validateAndUpdateExternalSources &&
            template.repo === this.viewService.getRepository().name &&
            template.type.toLowerCase() !== "git"
        ) {
            uri.addQueryParam("endpointId", this.viewService.getConnection()?.connectionId.toString());
        }

        if (template.repo) {
            uri.addQueryParam("repo", template.repo);
        }

        if (template.type) {
            uri.addQueryParam("type", template.type);
        }

        if (template.ref) {
            // Remove the existing branch parameter because that describes the pipeline definition branch
            // and we need to provide the branch of the template
            uri.removeQueryParam("branch");
            const branch = template.ref.replace("refs/heads/", "");
            uri.addQueryParam("branch", branch);
        }

        window.open(uri.absoluteUri, "_blank", "noopener");
    };

    public checkTemplate = (template: string) => {
        return this.templates[template]?.exists;
    };

    private showValidationPanel(errors: string[]) {
        this.layoutManager.renderCallout(onDismiss => (
            <CustomPanel ariaLabel={Resources.ValidatePanelTitle} size={ContentSize.Medium} onDismiss={onDismiss} lightDismiss={true}>
                <PanelHeader
                    titleProps={{ text: Resources.ValidatePanelTitle, size: TitleSize.Large }}
                    description={format(Resources.ValidatePanelDescription, this.viewService.getYamlPath())}
                    onDismiss={onDismiss}
                />
                <PanelContent>
                    {errors.length > 0 ? (
                        <div>
                            <label className="bolt-textfield-label secondary-text">{Resources.ValidationResultErrorsDetectedLabel}</label>
                            <div className="section-content padding-top-8">                                
                                <ValidationErrors errors={errors} />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-column full-height full-width flex-center justify-center">
                            <Status {...Statuses.Success} key={"success"} size={StatusSize.xl} className="margin-bottom-8" />
                            {Resources.ValidationResultSuccess}
                        </div>
                    )}
                </PanelContent>
                <PanelFooter
                    buttonProps={[
                        {
                            text: "Close",
                            onClick: onDismiss,
                            primary: false
                        }
                    ]}
                />
            </CustomPanel>
        ));
    }

    private showDialog(message: string) {
        this.layoutManager.renderCallout(onDismiss => (
            <Dialog
                onDismiss={onDismiss}
                lightDismiss={true}
                modal={true}
                contentSize={3}
                titleProps={{ text: Resources.Validation }}
                footerButtonProps={[
                    {
                        text: Resources.OK,
                        onClick: onDismiss,
                        primary: true
                    }
                ]}
            >
                {message}
            </Dialog>
        ));
    }

    private downloadFile(fileName: string, content: string) {
        const file = new Blob([content], { type: "text/plain" });

        // needed for IE10
        if (window.navigator && (window.navigator as any).msSaveBlob) {
            (window.navigator as any).msSaveBlob(file, fileName);
        } else {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(file);
            a.setAttribute("download", fileName);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }

    private async getYaml(): Promise<Run> {
        const definition = this.viewService.getDefinition();
        const isYamlDirty = this.viewService.getIsYamlDirty().value;
        const repositoryResources = {
            refName: this.viewService.getBranch()
        } as RepositoryResourceParameters;

        const resources: RunResourcesParameters = {
            pipelines: {},
            repositories: { self: repositoryResources },
            builds: {},
            containers: {},
            packages: {}
        };

        const runPipelineParams: RunPipelineParameters = {
            stagesToSkip: undefined as any,
            resources,
            templateParameters: {},
            variables: definition.variables,
            previewRun: true,
            yamlOverride: isYamlDirty ? this.viewService.getYamlContent().value : ""
        };

        const pipelinesClient = this.pageContext.getRestClient<IPipelinesRestClient>("IPipelinesRestClient");
        return pipelinesClient.runPipeline(runPipelineParams, definition.project.id, definition.id);
    }

    private saveYaml(commitDescriptor: CommitDescriptor, onDismiss: () => void): Promise<void> {
        this.isSaving.value = true;
        this.panelErrorMessage.value = "";

        const repository: RepositoryDescriptor = {
            connectionId: this.viewService.getConnection().connectionId,
            id: this.viewService.getRepository().id,
            sourceProviderType: this.viewService.getRepository().type
        };

        const content = this.editor!.getValue();
        return this.pageContext
            .getService<IYamlFileService>("IYamlFileService")
            .commitFile(
                repository,
                this.viewService.getBranch(),
                this.viewService.getYamlPath(),
                content,
                commitDescriptor,
                this.viewService.getYamlObjectId()
            )
            .then(
                (saveResult: SaveResult) => {
                    this.viewService.updateSavedYaml(saveResult, content);
                    this.isSaving.value = false;
                    this.panelErrorMessage.value = "";
                    onDismiss();
                },
                (error: Error) => {
                    this.panelErrorMessage.value = error.message;
                    this.isSaving.value = false;
                }
            );
    }

    private onSavePipelineSettings(changes: Partial<PipelineSettings>, onDismiss: () => void) {
        if (!changes || !Object.keys(changes).length) {
            return;
        }

        let requiresReload = false;
        const comments: string[] = [];
        const definition = { ...this.viewService.getDefinition() };
        this.isSaving.value = true;
        this.panelErrorMessage.value = "";

        // Update Queue Status
        if (changes.status !== undefined) {
            definition.queueStatus = changes.status;
            switch (changes.status) {
                case DefinitionQueueStatus.Disabled:
                    comments.push(LibraryResources.SavePipelineCommentDisable);
                    break;
                case DefinitionQueueStatus.Enabled:
                    comments.push(LibraryResources.SavePipelineCommentEnable);
                    break;
                case DefinitionQueueStatus.Paused:
                    comments.push(LibraryResources.SavePipelineCommentPause);
                    break;
                default:
                    comments.push(LibraryResources.SavePipelineCommentUnknownStatus);
                    break;
            }
        }

        // Update YAML file path
        if (changes.yamlFilePath !== undefined) {
            requiresReload = true;
            const yamlProcess = { ...definition.process } as YamlProcess;
            yamlProcess.yamlFilename = changes.yamlFilePath;
            definition.process = yamlProcess;
            comments.push(format(LibraryResources.SavePipelineCommentYamlFile, changes.yamlFilePath));
        }

        // Update Auto Work Item Linking Branch
        if (changes.witLinkingEnabled !== undefined || changes.witLinkingBranchFilter) {
            if (!definition.options) {
                definition.options = [];
            }
            let option = definition.options.filter(o => equals(o.definition.id, BuildOptionGUIDs.WitLinkingOptionGUID))[0];
            if (changes.witLinkingBranchFilter) {
                comments.push(format(LibraryResources.SavePipelineCommentUpdateAutoLinkBranch, changes.witLinkingBranchFilter));
                if (!option) {
                    option = {
                        definition: {
                            id: BuildOptionGUIDs.WitLinkingOptionGUID
                        },
                        inputs: {
                            branchFilters: JSON.stringify([changes.witLinkingBranchFilter])
                        },
                        enabled: true
                    };
                    definition.options.push(option);
                } else {
                    option.inputs = {
                        branchFilters: JSON.stringify([changes.witLinkingBranchFilter])
                    };
                }
            }
            if (changes.witLinkingEnabled !== undefined && option) {
                comments.push(format(LibraryResources.SavePipelineCommentEnableAutoLink, changes.witLinkingEnabled));
                option.enabled = changes.witLinkingEnabled;
            }
        }

        // Concatenate change comments for pipeline history.  Consider a dialog for a user to edit when/if needed.
        definition.comment = comments.join(". ");

        this.pipelineService.savePipeline(definition).then(
            (savedDefinition: BuildDefinition) => {
                this.showToast(Resources.PipelineSavedMessage);
                if (requiresReload) {
                    // TODO set isYamlDirty: false and isSaving: false so no lost changes prompt.
                    FastPageSwitch(this.pageContext, window.location.href, false);
                } else {
                    this.viewService.updateSavedDefinition(savedDefinition);
                    this.isSaving.value = false;
                    onDismiss();
                }
            },
            (error: Error) => {
                this.panelErrorMessage.value = (error && error.message) || LibraryResources.SavePipelineErrorMessage;
                this.isSaving.value = false;
            }
        );
    }

    private onSavePipelineVariables(variables: { [name: string]: BuildDefinitionVariable }, onDismiss: () => void) {
        const definitionToSave = { ...this.viewService.getDefinition(), variables, comment: Resources.VariablesPanelSaveComment };
        this.isSaving.value = true;
        this.panelErrorMessage.value = "";

        this.pipelineService.savePipeline(definitionToSave).then(
            (savedDefinition: BuildDefinition) => {
                this.showToast(Resources.PipelineSavedMessage);
                this.viewService.updateSavedDefinition(savedDefinition);
                this.isSaving.value = false;
                onDismiss();
            },
            (error: Error) => {
                this.panelErrorMessage.value = (error && error.message) || LibraryResources.SavePipelineErrorMessage;
                this.isSaving.value = false;
            }
        );
    }

    private showToast(message: string) {
        const globalMessageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        globalMessageService.addToast({
            message,
            duration: EditorConstants.ToastDuration
        });
    }

    private getRecommendedServiceConnection(): Promise<ServiceConnectionRecommendation> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.app-serviceconnections-recommendation-data-provider";
        const connection = this.viewService.getConnection();
        const parameters = {
            sourceProvider: this.viewService.getSourceProvider().getKey(),
            repositoryId: this.viewService.getRepository().id,
            repositoryName: this.viewService.getRepository().name,
            connectionId: connection.connectionId,
            strongBoxKey: "useWellKnownStrongBoxLocation",
            createOAuthConnectionIfNeeded: false,
            login: connection.login
        };

        return contributionService
            .getDataAsync<ServiceConnectionRecommendation>(dataProviderId, parameters, true)
            .then(serviceConnectionRecommendation => {
                if (!serviceConnectionRecommendation) {
                    const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                    throw new Error(exception && exception.message);
                }
                return serviceConnectionRecommendation;
            });
    }
}

function getOverridableVariables(variables: { [key: string]: BuildDefinitionVariable }): { name: string; variable: BuildDefinitionVariable }[] {
    return Object.keys(variables || {})
        .filter(key => variables[key].allowOverride)
        .map(key => {
            return { name: key, variable: { ...variables[key] } };
        });
}

const serviceName = "IPipelineEditorActionsViewService";

export function getPipelineEditorActionsViewService(pageContext: IVssPageContext): IPipelineEditorActionsViewService {
    return pageContext.getService<IPipelineEditorActionsViewService>(serviceName);
}

Services.add(serviceName, { serviceFactory: PipelineEditorActionsViewService });
